import { useCallback, useEffect, useRef } from 'react'
import { useWidgetContext, ToggleTypes } from '../../../context/WidgetContext'
import { X, ChatDots, Mic, Search, Image, CameraVideo, CodeSlash } from 'react-bootstrap-icons'
import { isFullscreenWidgetMode } from '../../../types/agent-mode'

export const PopupBtn = () => {
  const { toggle, hideChat, isWidgetOpen, isAIOpen, mode } = useWidgetContext()
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const isFullscreenLauncherMode = isFullscreenWidgetMode(mode)

  const closeClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (isFullscreenLauncherMode) {
      if (!isWidgetOpen || !isAIOpen) {
        toggle(ToggleTypes.widget, true)
        toggle(ToggleTypes.ai)
        return
      }

      toggle(ToggleTypes.ai, true)
      toggle(ToggleTypes.widget, false)
      return
    }

    if (!isWidgetOpen) {
      toggle(ToggleTypes.widget, true) // force open
      return
    }

    // If you want one-click close even when chat is active:
    hideChat()
    toggle(ToggleTypes.widget, false)
  }

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (isFullscreenLauncherMode) return
      if (!isWidgetOpen) return

      const target = event.target as Node | null
      if (!target) return

      // ✅ ignore clicks on the toggle button (light DOM)
      if (btnRef.current?.contains(target)) return

      // ✅ ignore clicks on the toggle button (shadow DOM / retargeted events)
      const path = (event.composedPath?.() ?? []) as EventTarget[]
      if (btnRef.current && path.includes(btnRef.current)) return

      const localRoot = btnRef.current?.closest('.osirus-agent') as HTMLElement | null
      if (localRoot && (localRoot.contains(target) || path.includes(localRoot))) return

      // ✅ (optional) if you use bootstrap portals outside shadow root, whitelist them too:
      const bsPortal =
        (document.querySelector('.offcanvas') as HTMLElement | null) ||
        (document.querySelector('.modal') as HTMLElement | null)
      if (bsPortal && (bsPortal.contains(target) || path.includes(bsPortal))) return

      // outside → close widget
      hideChat()
      toggle(ToggleTypes.widget, false)
    },
    [isFullscreenLauncherMode, isWidgetOpen, hideChat, toggle]
  )

  useEffect(() => {
    // ✅ use click, not mousedown (avoids the “mousedown beats onClick” issue)
    document.addEventListener('click', handleOutsideClick)
    return () => document.removeEventListener('click', handleOutsideClick)
  }, [handleOutsideClick])

  if (isFullscreenLauncherMode && isAIOpen) return null

  return (
    <button
      ref={btnRef}
      className="osirus-agent-logo"
      onMouseDown={(e) => e.stopPropagation()} // ✅ stops any document mousedown listeners elsewhere too
      onClick={closeClick}
    >
      {!isFullscreenLauncherMode && isWidgetOpen ? (
        <div className="active-close">
          <X />
        </div>
      ) : mode === 'speech' ? (
        <Mic className="osirus-agent-chat-icon" />
      ) : mode === 'search' ? (
        <Search className="osirus-agent-chat-icon" />
      ) : mode === 'code' ? (
        <CodeSlash className="osirus-agent-chat-icon" />
      ) : mode === 'image' ? (
        <Image className="osirus-agent-chat-icon" />
      ) : mode === 'video' ? (
        <CameraVideo className="osirus-agent-chat-icon" />
      ) : (
        <ChatDots className="osirus-agent-chat-icon" />
      )}
    </button>
  )
}
